import { FC } from 'react'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import { FormElement } from '@mobileTypes'
import { getFormElementIcon, getFormElementSummary } from '@mobileConstants'
import { Box, Paper, Typography, IconButton, Chip } from '@mui/material'
import { DragIndicator, Delete, Edit } from '@mui/icons-material'

interface ConfiguredFormPanelProps {
  elements: FormElement[]
  bottomElements: FormElement[]
  onEditElement: (index: number, isBottom: boolean) => void
  onDeleteElement: (index: number, isBottom: boolean) => void
}

const ElementItem: FC<{
  element: FormElement
  index: number
  isBottom: boolean
  onEdit: () => void
  onDelete: () => void
}> = ({ element, index, isBottom, onEdit, onDelete }) => {
  return (
    <Draggable draggableId={`${isBottom ? 'BOTTOM' : 'MAIN'}:${index}`} index={index}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          sx={{
            mb: 1,
            opacity: snapshot.isDragging ? 0.5 : 1,
          }}
        >
          <Paper
            variant='outlined'
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              backgroundColor: snapshot.isDragging ? 'action.hover' : 'background.paper',
              borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
            }}
          >
            <Box
              {...provided.dragHandleProps}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'grab',
                color: 'text.secondary',
              }}
            >
              <DragIndicator fontSize='small' />
            </Box>

            <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
              {getFormElementIcon(element.type, { fontSize: 'small' })}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant='body2' fontWeight='500' noWrap>
                  {getFormElementSummary(element)}
                </Typography>
                <Chip label={element.type} size='small' sx={{ height: 18, fontSize: '0.65rem' }} />
              </Box>
              {element.type === 'FIELD' && element.data && (
                <Typography variant='caption' color='text.secondary' noWrap>
                  {element.data.code || element.data.objectCode}
                </Typography>
              )}
              {element.type === 'BUTTON' && (element.command || element.actions?.length) && (
                <Typography variant='caption' color='text.secondary' noWrap>
                  {element.command ? `${element.command.name} (${element.command.objectCode})` : `${element.actions?.length} actions`}
                </Typography>
              )}
              {element.type === 'SWIPE' && (element.command || element.actions?.length) && (
                <Typography variant='caption' color='text.secondary' noWrap>
                  {element.command ? element.command.name : `${element.actions?.length} actions`}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {element.type !== 'DIVIDER' && (
                <IconButton size='small' onClick={onEdit} title='Edit'>
                  <Edit fontSize='small' />
                </IconButton>
              )}
              <IconButton size='small' color='error' onClick={onDelete} title='Delete'>
                <Delete fontSize='small' />
              </IconButton>
            </Box>
          </Paper>
        </Box>
      )}
    </Draggable>
  )
}

export const ConfiguredFormPanel: FC<ConfiguredFormPanelProps> = ({
  elements,
  bottomElements,
  onEditElement,
  onDeleteElement,
}) => {
  return (
    <Paper
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant='h6'>Configured Form</Typography>
        <Typography variant='caption' color='text.secondary'>
          Reorder elements by dragging
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {/* Main Elements */}
        <Box sx={{ mb: 3 }}>
          <Typography variant='subtitle2' sx={{ mb: 1.5, color: 'text.secondary' }}>
            Form Elements
          </Typography>
          <Droppable droppableId='MAIN_ELEMENTS' type='ELEMENTS'>
            {(provided, snapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  minHeight: 100,
                  borderRadius: 1,
                  border: '2px dashed',
                  borderColor: snapshot.isDraggingOver ? 'primary.main' : 'divider',
                  backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                  p: 1,
                  transition: 'all 0.2s',
                }}
              >
                {elements.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 80,
                    }}
                  >
                    <Typography variant='body2' color='text.secondary'>
                      Drag elements here
                    </Typography>
                  </Box>
                ) : (
                  elements.map((element, index) => (
                    <ElementItem
                      key={index}
                      element={element}
                      index={index}
                      isBottom={false}
                      onEdit={() => onEditElement(index, false)}
                      onDelete={() => onDeleteElement(index, false)}
                    />
                  ))
                )}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </Box>

        {/* Bottom Elements */}
        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1.5, color: 'text.secondary' }}>
            Bottom Elements (Action Buttons)
          </Typography>
          <Droppable droppableId='BOTTOM_ELEMENTS' type='ELEMENTS'>
            {(provided, snapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  minHeight: 80,
                  borderRadius: 1,
                  border: '2px dashed',
                  borderColor: snapshot.isDraggingOver ? 'primary.main' : 'divider',
                  backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                  p: 1,
                  transition: 'all 0.2s',
                }}
              >
                {bottomElements.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 60,
                    }}
                  >
                    <Typography variant='body2' color='text.secondary'>
                      Drag button elements here
                    </Typography>
                  </Box>
                ) : (
                  bottomElements.map((element, index) => (
                    <ElementItem
                      key={index}
                      element={element}
                      index={index}
                      isBottom={true}
                      onEdit={() => onEditElement(index, true)}
                      onDelete={() => onDeleteElement(index, true)}
                    />
                  ))
                )}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </Box>
      </Box>
    </Paper>
  )
}
